import { useTranslation } from 'react-i18next';
import { formatMinor, signOf } from '../format';
import { currencyLabel } from './labels';

/**
 * Money renders ONE amount the server sent, with the currency the server named.
 *
 * ⚠ IT IS THE ONLY WAY AN AMOUNT REACHES THE SCREEN IN THIS APP, and that is what makes
 * «no money is computed client-side» checkable rather than merely claimed: there is one
 * component, it takes a single integer of minor units, and it has nowhere to put a second
 * operand. A screen that wanted to show a difference would have to compute it first, and
 * `src/money-arithmetic.test.ts` fails on the attempt.
 *
 * The colour is §2.3's and the global chrome's: «Positive balances are shown in green,
 * negative in red». It comes from a COMPARISON (`signOf`), never from a computation, and it
 * is opt-in — a charge of 250,00 is not «bad news in red», it is simply what the tariff
 * costs, so only the figures that represent a subscriber's standing carry it.
 */
export function Money({
  minor,
  currency,
  strong,
  colored,
}: {
  minor: number | null | undefined;
  currency: string;
  /** The big legible figure of P7-defaults — the balance, the resume amount. */
  strong?: boolean;
  /** Green when positive, red when negative. For balances and availability only. */
  colored?: boolean;
}) {
  const { t } = useTranslation();
  const classes = ['pa-money'];
  if (strong) classes.push('pa-money-strong');
  if (colored) classes.push(`pa-money-${signOf(minor)}`);
  const symbol = currencyLabel(currency, t);
  return (
    <span className={classes.join(' ')}>
      {formatMinor(minor)}
      {symbol ? <span className="pa-money-currency">{symbol}</span> : null}
    </span>
  );
}
